/* eslint-disable @typescript-eslint/naming-convention */
import { MoreInfoTooltip, useFetch, Widths } from '@brandfolder/react';
import React, { FunctionComponent } from 'react';

import {
  CustomFieldKeysListResponse,
  DependentFieldsResponseObject
} from '@api/v4/resources/CustomFieldKeysTypes';
import { getDependentCFKeys } from '@helpers/dependent_custom_fields';

import { DependencyViewAccordionPanel } from './DependencyViewAccordionPanel';

import './styles/dependency-view-accordion.scss';
import { determineUGTLocale } from '@components/asset/modal/tabs/edit/helpers';

const VALUE_TOOLTIP_TEXT =
  bfTranslate('The parent value that, when selected, will display the dependent custom field.');

interface DependentRow {
  value: string;
  dependentCustomFields: string[]
}

export interface PanelRow {
  customFieldName: string;
  dependentRows: DependentRow[],
}

export const DependencyViewAccordion: FunctionComponent = () => {
  const customFieldKeysFetch = useFetch<CustomFieldKeysListResponse>({
    url: `api/v4/brandfolders/${BFG.resource?.key}/custom_field_keys`,
    include: 'dependent_custom_fields',
    params: {
      ugt_locale: determineUGTLocale()
    }
  });
  const dcfIds = getDependentCFKeys(customFieldKeysFetch.response?.included);
  const filteredCustomFields = customFieldKeysFetch.response?.data.filter(({ id }) => !dcfIds?.includes(id));

  const dcfMatchingValues = (val: string): DependentFieldsResponseObject[] =>
    customFieldKeysFetch.response?.included.filter(({ attributes }) => attributes.value === val);

  const cfMatchingValues = (val: string): DependentFieldsResponseObject[] =>
    customFieldKeysFetch.response?.data.filter(({ id }) =>
      getDependentCFKeys(dcfMatchingValues(val)).includes(id));

  const flattenedAccordionPanels = filteredCustomFields?.reduce((panels, field) => {
    if (!panels[field.id]) {
      Object.assign(panels, {
        [field.id]: {
          customFieldName: field.attributes.name,
          dependentRows: field.attributes.allowed_values.map((value) => {
            return ({
              value: value,
              dependentCustomFields: cfMatchingValues(value).map(({ attributes }) => attributes.name),
            });
          }),
        }
      });
    }
    return panels;
  }, {} as PanelRow);

  if (!customFieldKeysFetch.response) return null;

  return (
    <table className='dcf-accordion'>
      <thead className='dcf-accordion-heading'>
        <tr>
          <th>{bfTranslate('Custom field')}</th>
          <th>
            {bfTranslate('Value')}
            <MoreInfoTooltip
              iconLabel='parent value'
              id='parent-value'
              tooltip={VALUE_TOOLTIP_TEXT}
              width={Widths.Small}
            />
          </th>
          <th>{bfTranslate('Dependent custom fields')}</th>
        </tr>
      </thead>
      <tbody>
        {Object.keys(flattenedAccordionPanels).map((id) =>
          <DependencyViewAccordionPanel
            key={flattenedAccordionPanels[id].customFieldName}
            panelRow={flattenedAccordionPanels[id]}
          />
        )}
      </tbody>
    </table>
  );
};
